import React from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { compose } from 'redux';
import Table from '../../../../common/table/Table';
import RowClick from '../../../../common/RowClick';
import {
  fetchMarketAnomaly,
  REDUCER_NAME,
  subscribeRealtimeTickMarketAnomaly,
} from '../reducer';
import { includes } from 'lodash';
import TextBlink from '../../../../common/table/TextBlink';
import {
  formatPrice,
  formatTextFloat,
  formatVolume,
  formatPercent,
} from '../../../../helpers/Text';
import { cellNegtiveRed } from '../../../../common/table/CustomCellTable';
import { getDataFollowKeyByDot, getRanking } from '../../../../helpers/Common';
import { getColorPrice } from '../../../../helpers/Color';
import MessageHub from '../../../../../core/signalr/SignalrMessageHub';
import { ChanelConfig } from '../../../../../configs/GlobalConfig';
import CellHover from '../../../../market/watchlist/summary/CellHover';
import TradingViewUrl from '../../../../common/TradingViewUrl';

const getClassName = (item, listColumn) => {
  return getColorPrice(
    getDataFollowKeyByDot(item, listColumn.PRICE),
    getDataFollowKeyByDot(item, listColumn.REF_PRICE),
    getDataFollowKeyByDot(item, listColumn.CEILING_PRICE),
    getDataFollowKeyByDot(item, listColumn.FLOOR_PRICE),
    'text',
  );
};

class MarketAbnormallyTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
    };
  }

  componentDidMount() {
    MessageHub.subscribe(ChanelConfig.TickChanel, this.dispatchDataRealtime);
  }

  dispatchDataRealtime = data => {
    const { subscribeRealtimeTickMarketAnomaly } = this.props;
    subscribeRealtimeTickMarketAnomaly(data);
  };

  getDataFromRedux = () => {
    return state => state[REDUCER_NAME].listObjectDataMarketAbnormalyByTicker;
  };

  getSchema = () => {
    const { title: tabTitle, schemaKey } = this.props;

    return Object.keys(schemaKey).map((item, index) => {
      const key = schemaKey[item];
      const title = `${tabTitle}.${item}`;
      const result = {
        key,
        title,
      };

      if (index === 0) {
        return {
          ...result,
          render: (text, item) => (
            <CellHover organCode={item.organCode} isShowDeleteIcon={false}>
              <TextBlink item={item} tradingDate="tradingDate">
                <TradingViewUrl code={item.organCode} />
                &nbsp;{text}
              </TextBlink>
            </CellHover>
          ),
        };
      }

      if (
        includes(
          [
            schemaKey.VOL,
            schemaKey.VOL_PREVIOUS,
            schemaKey.VOL_AVG,
            schemaKey.AVG_VOL_1M,
          ],
          key,
        )
      ) {
        return {
          ...result,
          render: (text, item) => {
            return (
              <TextBlink item={item} tradingDate="tradingDate">
                {formatTextFloat(formatVolume(text))}
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.PRICE_PREV, schemaKey.PRICE_AVG], key)) {
        return {
          ...result,
          render: (text, item) => {
            return (
              <TextBlink item={item} tradingDate="tradingDate">
                {formatTextFloat(formatPrice(text))}
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.PRICE], key)) {
        return {
          ...result,
          render: (text, item) => {
            const className = getClassName(item, schemaKey);
            return (
              <TextBlink
                item={item}
                tradingDate="tradingDate"
                className={className}
              >
                {formatTextFloat(formatPrice(text))}
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.RSI14], key)) {
        return {
          ...result,
          render: text => {
            return (
              <TextBlink item={item} tradingDate="tradingDate">
                {formatTextFloat(text)}
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.MA20], key)) {
        return {
          ...result,
          render: text => {
            return (
              <TextBlink item={item} tradingDate="tradingDate">
                {formatTextFloat(formatPrice(text))}
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.PE, schemaKey.PB], key)) {
        return {
          ...result,
          render: (text, item) => {
            return (
              <TextBlink item={item} tradingDate="tradingDate">
                {cellNegtiveRed(text)}
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.AVG_RETURN], key)) {
        const { currentTab, filterData } = this.props;
        const valueFromItem = item =>
          getDataFollowKeyByDot(
            item,
            `${schemaKey.AVG_RETURN}.${filterData[currentTab].filterLineOne}`,
          );
        return {
          ...result,
          valueFromItem,
          render: (text, item) => {
            const data = valueFromItem(item);

            return (
              <TextBlink item={item} tradingDate="tradingDate">
                <div className={data < 0 ? 'text-s-color-3' : ''}>
                  {formatPercent(data)}%
                </div>
              </TextBlink>
            );
          },
        };
      }

      if (includes([schemaKey.RANKING], key)) {
        return {
          ...result,
          thTooltip: I18n.t('table.strategy.ranking'),
          disableResize: true,
          render: (rank, item) => {
            const totalRank = getDataFollowKeyByDot(item, schemaKey.TOTAL_RANK);
            return (
              <TextBlink item={item} tradingDate="tradingDate">
                {getRanking(rank, totalRank)}
              </TextBlink>
            );
          },
        };
      }

      return {
        ...result,
        render: text => <TextBlink>{formatTextFloat(text)}</TextBlink>,
      };
    });
  };

  handleScroll = e => {
    const { page } = this.state;
    const { totalPage, isLoading, fetchMarketAnomaly } = this.props;
    const bottom =
      e.target.scrollHeight - Math.round(e.target.scrollTop) ===
      e.target.clientHeight;

    if (page >= totalPage || isLoading) return;
    if (bottom) {
      const newPage = page + 1;
      this.setState({
        page: newPage,
      });
      fetchMarketAnomaly(true, newPage);
    }
  };

  render() {
    const {
      isLoading,
      listHideColumn,
      listDataMarketAbnormalyTicker,
      onSort,
    } = this.props;

    return (
      <RowClick>
        {onRowClick => (
          <Table
            onSort={onSort}
            clickRow={onRowClick}
            thValign="top"
            isLoading={isLoading}
            ids={listDataMarketAbnormalyTicker}
            resizable={true}
            columnDraggable={false}
            onScrollFunction={this.handleScroll}
            rowDraggable={false}
            hideColumns={listHideColumn}
            getDataFromRedux={this.getDataFromRedux()}
            schema={this.getSchema()}
          />
        )}
      </RowClick>
    );
  }
}

const mapStateToProps = state => {
  return {
    listDataMarketAbnormalyTicker:
      state[REDUCER_NAME].listDataMarketAbnormalyTicker,
    isLoading: state[REDUCER_NAME].isLoading,
    i18n: state.i18n,
    totalPage: state[REDUCER_NAME].marketAnomalyTotalPage,
  };
};

const mapDispatchToProps = {
  subscribeRealtimeTickMarketAnomaly,
  fetchMarketAnomaly,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
);

export default enhance(MarketAbnormallyTable);
